import React from 'react';
import {
  Image,
  ImageSourcePropType,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import { colors, radii } from '../../theme';
import Icon, { IconName } from '../ui/Icon';

type Props = {
  /** Real photography, when there is any. */
  image?: ImageSourcePropType;
  /** Drawn instead when there is not. */
  icon: IconName;
  /** Tinted panel behind the fallback glyph. */
  tone?: 'muted' | 'inverse';
  style?: StyleProp<ViewStyle>;
  rounded?: boolean;
};

/**
 * The picture slot on a card.
 *
 * The catalogue has no photography yet, so this degrades to a tinted panel
 * carrying the category's glyph rather than leaving a hole or shipping a
 * stretched stock image. Set `image` on the service and the same slot renders
 * the photo instead - no other change to the card.
 */
function ArtPanel({ image, icon, tone = 'muted', style, rounded = true }: Props) {
  const inverse = tone === 'inverse';

  /*
   * The frame is always a View, even when there is a photo, and the photo
   * fills it absolutely. Handing the same `style` to an `Image` instead would
   * not typecheck - `ViewStyle` and `ImageStyle` disagree about `overflow` -
   * and callers should not have to know which branch they are styling.
   */
  return (
    <View
      style={[
        styles.panel,
        rounded ? styles.rounded : null,
        image
          ? null
          : { backgroundColor: inverse ? colors.primaryPressed : colors.surfaceAccent },
        style,
      ]}
    >
      {image ? (
        <Image
          source={image}
          style={styles.photo}
          resizeMode="cover"
          /*
           * The two flags that decide what a list of these costs.
           *
           * `resizeMethod="resize"` makes Android downsample while decoding,
           * so a 480px asset drawn into a 96pt box allocates a bitmap near the
           * box's size instead of the file's. Without it the full image is
           * decoded and then scaled at draw time - the memory is spent either
           * way, and with sixty rows on the Services tab that is what the
           * scrolling stutter was made of. Ignored on iOS, which already does
           * this.
           *
           * `fadeDuration={0}` drops Android's default 300ms cross-fade on
           * every image that appears. In a scrolling list that animation runs
           * for each row as it recycles into view, which is both a frame cost
           * and a visible flicker; the picture is local, so there is nothing
           * to fade *in* from.
           */
          resizeMethod="resize"
          fadeDuration={0}
        />
      ) : (
        <Icon
          name={icon}
          size={34}
          color={inverse ? colors.inkInverseMuted : colors.primary}
          strokeWidth={1.4}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  panel: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  rounded: {
    borderRadius: radii.md,
    overflow: 'hidden',
  },
  /*
   * Sized as a plain percentage of the frame rather than by absolute insets.
   * An absolutely positioned image with no width or height of its own can fall
   * back to its intrinsic size, which anchors the photo top-left inside the
   * frame and shows a magnified corner of it instead of the whole picture.
   */
  photo: {
    width: '100%',
    height: '100%',
  },
});

/*
 * Memoised: it holds an `Image`, and it is rendered once per row in lists that
 * re-render whenever the cart or the search text changes. `image` is a
 * `require()` id - a stable number - so the comparison is cheap and reliable.
 */
export default React.memo(ArtPanel);
